import { ContainerCutter } from './types';

/** WebM Cluster element ID (0x1F43B675) — the fragment boundary marker. */
const CLUSTER_ID_BYTES = Uint8Array.from([0x1f, 0x43, 0xb6, 0x75]);
/** Matroska Segment element ID; Clusters are its children. */
const SEGMENT_ID = 0x18538067;

const EMPTY = new Uint8Array(0);

interface ElementHeader {
  id: number;
  headerLength: number;
  size: number;
  sizeIsUnknown: boolean;
}

const concatBytes = (a: Uint8Array, b: Uint8Array): Uint8Array => {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
};

/**
 * ContainerCutter for the WebM/Matroska streams MediaRecorder produces.
 *
 * Boundaries are found by an incremental EBML element walk: element IDs are
 * only interpreted at element boundaries, so a 0x1F43B675 byte pattern inside
 * a SimpleBlock payload can never be mistaken for a Cluster (a naive byte scan
 * could cut mid-payload — silent corruption the backend would accept and
 * transcribe). The init segment is whatever precedes the first Cluster; it
 * measured 146 bytes on Chromium 149 and WebKit 26.5, but that is a muxer
 * detail and is never assumed — only the scan result is used.
 *
 * Byte splicing is viable at all only because MediaRecorder writes the Segment
 * and every Cluster unknown-size, so emitted chunks need no length fixups.
 */
export class WebmClusterCutter implements ContainerCutter {
  /** Un-emitted stream bytes. The head chunk's slice includes the init segment. */
  private buffer: Uint8Array = EMPTY;
  /**
   * Walker offset into `buffer`. May point past the current end while an
   * element whose payload has not fully arrived is being skipped.
   */
  private parsePos = 0;
  private insideSegment = false;
  private initSegment: Uint8Array | null = null;
  /** Offsets into `buffer` of every scanned Cluster start, ascending. */
  private clusterStarts: number[] = [];
  /** False until the head chunk (which carries the init segment inline) is emitted. */
  private emittedHead = false;
  /**
   * True after flush() discarded buffered parse state. Appending then would
   * parse a new stream's bytes against the retired stream's walker position
   * and init segment — silent corruption — so append() throws until reset().
   */
  private flushed = false;

  append(bytes: Uint8Array): void {
    if (this.flushed) {
      throw new Error('WebmClusterCutter: append after flush; reset() first');
    }
    if (bytes.length === 0) {
      return;
    }
    this.buffer = concatBytes(this.buffer, bytes);
    this.advance();
  }

  /**
   * Number of COMPLETE clusters buffered — a cluster counts only once the
   * start of the one that follows it has been scanned.
   */
  completeFragmentCount(): number {
    return Math.max(0, this.clusterStarts.length - 1);
  }

  bufferedByteLength(): number {
    return this.buffer.length;
  }

  cut(fragmentCount: number): Uint8Array | null {
    if (!Number.isInteger(fragmentCount) || fragmentCount < 1) {
      throw new Error(
        `WebmClusterCutter: invalid fragmentCount ${fragmentCount}`
      );
    }
    if (this.completeFragmentCount() < fragmentCount) {
      return null;
    }
    const cutPos = this.clusterStarts[fragmentCount];
    const head = this.buffer.slice(0, cutPos);
    const emitted = this.emittedHead
      ? concatBytes(this.requireInitSegment(), head)
      : head;

    this.buffer = this.buffer.slice(cutPos);
    this.parsePos -= cutPos;
    this.clusterStarts = this.clusterStarts
      .slice(fragmentCount)
      .map((offset) => offset - cutPos);
    this.emittedHead = true;
    return emitted;
  }

  cutWithin(maxBytes: number, maxFragments = Infinity): Uint8Array | null {
    const prefix = this.emittedHead ? this.requireInitSegment().length : 0;
    const limit = Math.min(maxFragments, this.completeFragmentCount());
    let fragmentCount = 0;
    for (let k = 1; k <= limit; k++) {
      if (prefix + this.clusterStarts[k] > maxBytes) {
        break;
      }
      fragmentCount = k;
    }
    return fragmentCount === 0 ? null : this.cut(fragmentCount);
  }

  flush(): Uint8Array | null {
    if (this.buffer.length === 0) {
      return null;
    }
    const tail = this.emittedHead
      ? concatBytes(this.requireInitSegment(), this.buffer)
      : this.buffer;
    this.buffer = EMPTY;
    this.parsePos = 0;
    this.clusterStarts = [];
    this.emittedHead = true;
    this.flushed = true;
    return tail;
  }

  /** Byte length of the scanned init segment, or null before it was found. */
  initSegmentByteLength(): number | null {
    return this.initSegment ? this.initSegment.length : null;
  }

  reset(): void {
    this.buffer = EMPTY;
    this.parsePos = 0;
    this.insideSegment = false;
    this.initSegment = null;
    this.clusterStarts = [];
    this.emittedHead = false;
    this.flushed = false;
  }

  private requireInitSegment(): Uint8Array {
    if (!this.initSegment) {
      throw new Error('WebmClusterCutter: init segment not scanned yet');
    }
    return this.initSegment;
  }

  /**
   * Resumable walk. Stops (without consuming) whenever an element header is
   * not fully buffered yet; the next append resumes from the same position.
   */
  private advance(): void {
    for (;;) {
      if (!this.insideSegment) {
        // Top level: skip sized elements (the EBML header) until the Segment,
        // then descend into it — its children are what the cutter fragments.
        const header = this.readElementHeader(this.parsePos);
        if (header === null) {
          return;
        }
        if (header.id === SEGMENT_ID) {
          this.parsePos += header.headerLength;
          this.insideSegment = true;
          continue;
        }
        if (header.sizeIsUnknown) {
          throw new Error(
            `WebmClusterCutter: unknown-size element 0x${header.id.toString(16)} before Segment`
          );
        }
        this.parsePos += header.headerLength + header.size;
        continue;
      }

      if (this.parsePos + CLUSTER_ID_BYTES.length > this.buffer.length) {
        return;
      }
      if (this.matchesClusterId(this.parsePos)) {
        const header = this.readElementHeader(this.parsePos);
        if (header === null) {
          return;
        }
        if (this.initSegment === null) {
          this.initSegment = this.buffer.slice(0, this.parsePos);
        }
        this.clusterStarts.push(this.parsePos);
        // Unknown-size cluster (the MediaRecorder case): walk its children —
        // the next Cluster ID at element level ends it. Known-size: skip whole.
        this.parsePos += header.sizeIsUnknown
          ? header.headerLength
          : header.headerLength + header.size;
        continue;
      }

      const child = this.readElementHeader(this.parsePos);
      if (child === null) {
        return;
      }
      if (child.sizeIsUnknown) {
        throw new Error(
          `WebmClusterCutter: unknown-size non-Cluster element 0x${child.id.toString(16)} at offset ${this.parsePos}`
        );
      }
      this.parsePos += child.headerLength + child.size;
    }
  }

  private matchesClusterId(pos: number): boolean {
    for (let i = 0; i < CLUSTER_ID_BYTES.length; i++) {
      if (this.buffer[pos + i] !== CLUSTER_ID_BYTES[i]) {
        return false;
      }
    }
    return true;
  }

  /** Parse an element header at `pos`; null = not enough bytes buffered yet. */
  private readElementHeader(pos: number): ElementHeader | null {
    if (pos >= this.buffer.length) {
      return null;
    }
    const lead = this.buffer[pos];
    let idLength: number;
    if (lead & 0x80) {
      idLength = 1;
    } else if (lead & 0x40) {
      idLength = 2;
    } else if (lead & 0x20) {
      idLength = 3;
    } else if (lead & 0x10) {
      idLength = 4;
    } else {
      throw new Error(
        `WebmClusterCutter: invalid element ID lead byte 0x${lead.toString(16)} at offset ${pos}`
      );
    }
    if (pos + idLength > this.buffer.length) {
      return null;
    }
    let id = 0;
    for (let i = 0; i < idLength; i++) {
      id = id * 256 + this.buffer[pos + i];
    }

    const sizePos = pos + idLength;
    if (sizePos >= this.buffer.length) {
      return null;
    }
    const sizeLead = this.buffer[sizePos];
    let sizeLength = 0;
    for (let i = 0; i < 8; i++) {
      if (sizeLead & (0x80 >> i)) {
        sizeLength = i + 1;
        break;
      }
    }
    if (sizeLength === 0) {
      throw new Error(
        `WebmClusterCutter: invalid size vint lead byte 0x00 at offset ${sizePos}`
      );
    }
    if (sizePos + sizeLength > this.buffer.length) {
      return null;
    }
    const leadMask = 0xff >> sizeLength;
    // Sizes are accumulated by multiplication, not shifts — JS bitwise ops
    // truncate to 32 bits and size vints can be up to 8 bytes.
    let size = sizeLead & leadMask;
    let allOnes = (sizeLead & leadMask) === leadMask;
    for (let i = 1; i < sizeLength; i++) {
      const byte = this.buffer[sizePos + i];
      size = size * 256 + byte;
      if (byte !== 0xff) {
        allOnes = false;
      }
    }
    return {
      id,
      headerLength: idLength + sizeLength,
      size,
      sizeIsUnknown: allOnes,
    };
  }
}
